import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["placement"],
  _excluded2 = ["badgeClassName", "badgeDescription", "badgeLabel", "children", "className", "placement", "showBadge", "size"];
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Component, cloneElement } from 'react';
import styled, { css, keyframes } from 'styled-components';
import TwoWayTransition from '../transitions/TwoWayTransition';
import { propTypeForSizes, toShorthandSize } from '../utils/propTypes/tshirtSize';
import { uniqueId } from '../utils/underscore';
import UIBadge from './UIBadge';
import { warnIfFragment } from '../utils/devWarnings';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BADGE_SIZE = {
  default: 'count',
  md: 'count',
  sm: 'dot'
};
const popIn = keyframes(["0%{animation-timing-function:cubic-bezier(.16,.84,.44,1);transform:scale(1);}50%{animation-timing-function:cubic-bezier(.89,.03,.68,.22);transform:scale(1.25);}100%{transform:scale(1);}"]);
const scaleIn = keyframes(["0%{transform:scale(.75);}100%{transform:scale(1);}"]);
const fadeIn = keyframes(["0%{opacity:0;}33%{opacity:1;}"]);
const scaleOut = keyframes(["0%{transform:scale(1);}75%{transform:scale(.75);}"]);
const fadeOut = keyframes(["0%,25%{opacity:1;}100%{opacity:0;}"]);
const NotificationBadgeTransitionWrapper = styled.div.withConfig({
  displayName: "UINotificationBadge__NotificationBadgeTransitionWrapper",
  componentId: "sc-1unv069-0"
})([".hs-private.hs-private.hs-private & .private-notification__badge{", ";opacity:", ";}"], ({
  firstRender,
  open,
  transitionActive
}) => {
  if (!transitionActive) return '';
  if (firstRender && open) return css(["animation:", " 150ms;"], popIn);
  return open ? css(["animation:", " 300ms cubic-bezier(0.2,0.9,0.3,2),", " 150ms cubic-bezier(0.42,0,0.58,1);"], scaleIn, fadeIn) : css(["animation:", " 150ms cubic-bezier(0.89,0.03,0.68,0.22),", " 200ms cubic-bezier(0.42,0,0.58,1);"], scaleOut, fadeOut);
}, ({
  open
}) => open ? 1 : 0);
const getTransitionDuration = (firstRender, open) => {
  if (firstRender && open) return 150;
  return open ? 300 : 200;
};
const Wrapper = styled.div.withConfig({
  displayName: "UINotificationBadge__Wrapper",
  componentId: "sc-1unv069-1"
})([".hs-private.hs-private.hs-private &{position:relative;display:inline-flex;align-items:baseline;line-height:1;}"]);
const insetBadgeMixin = css(["position:absolute;left:75%;top:5%;"]);
const getOutsetBadgeOffset = ({
  use
}) => use === 'dot' ? '-5px' : '-10.5px';
const outsetBadgeMixin = css(["position:absolute;right:", ";top:", ";"], getOutsetBadgeOffset, getOutsetBadgeOffset);
const Badge = styled(_ref => {
  let rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(UIBadge, Object.assign({}, rest));
}).withConfig({
  displayName: "UINotificationBadge__Badge",
  componentId: "sc-1unv069-2"
})([".hs-private.hs-private.hs-private &{", ";}"], ({
  placement
}) => {
  if (placement === 'inset') return insetBadgeMixin;
  if (placement === 'outset') return outsetBadgeMixin;
  return null;
});
export default class UINotificationBadge extends Component {
  constructor(props) {
    super(props);
    this._descriptionId = uniqueId('notification-badge-');
    this._hasRendered = false;
  }
  componentDidMount() {
    this._hasRendered = true;
  }
  render() {
    const _this$props = this.props,
      {
        badgeClassName,
        badgeDescription,
        badgeLabel,
        children,
        className,
        placement = 'outset',
        showBadge = true,
        size = 'md'
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded2);
    const firstRender = !this._hasRendered;
    warnIfFragment(children, UINotificationBadge.displayName = 'UINotificationBadge');
    return /*#__PURE__*/_jsxs(Wrapper, Object.assign({}, rest, {
      className: classNames('private-notification__wrapper', className),
      children: [/*#__PURE__*/cloneElement(children, {
        'aria-describedby': showBadge ? this._descriptionId : undefined
      }), /*#__PURE__*/_jsx(TwoWayTransition, {
        duration: getTransitionDuration(firstRender, showBadge),
        firstRender: firstRender,
        transitionOnMount: true,
        Wrapper: NotificationBadgeTransitionWrapper,
        children: showBadge && /*#__PURE__*/_jsxs(Badge, {
          className: classNames('private-notification__badge', badgeClassName),
          id: this._descriptionId,
          placement: placement,
          use: BADGE_SIZE[toShorthandSize(size)],
          children: [badgeLabel, /*#__PURE__*/_jsx("span", {
            className: "sr-only",
            children: badgeDescription
          })]
        })
      })]
    }));
  }
}
UINotificationBadge.defaultProps = {
  placement: 'outset',
  size: 'md',
  showBadge: true
};
UINotificationBadge.displayName = 'UINotificationBadge';
UINotificationBadge.propTypes = {
  badgeClassName: PropTypes.string,
  badgeDescription: PropTypes.string.isRequired,
  badgeLabel: PropTypes.node,
  children: PropTypes.element.isRequired,
  placement: PropTypes.oneOf(['inset', 'outset', 'inline']),
  size: propTypeForSizes(['sm', 'md'], ['default']),
  showBadge: PropTypes.bool
};